import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail, downloadReport } from "@/lib/api";
import { inr, num, fmtDate, today, billNo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import PaymentMethods from "@/components/PaymentMethods";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, ShoppingCart, FileDown, FileSpreadsheet, Eye, Pencil, ChevronsUpDown, Check, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const blankItem = { product_name: "", unit: "Piece", quantity: "", cost: "", net_price: "" };

export default function PurchaseEntry() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [supplierId, setSupplierId] = useState("");
  const [date, setDate] = useState(today());
  const [billNumber, setBillNumber] = useState("");
  const [items, setItems] = useState([{ ...blankItem }]);
  const [expense, setExpense] = useState("");
  const [roundOff, setRoundOff] = useState("");
  const [memo, setMemo] = useState("");
  const [payType, setPayType] = useState("credit");
  const [methods, setMethods] = useState([]);
  const [viewBill, setViewBill] = useState(null);
  const [editId, setEditId] = useState(null);
  const [supOpen, setSupOpen] = useState(false);

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-min"], queryFn: async () => (await api.get("/suppliers")).data });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: async () => (await api.get("/purchases/units")).data });
  const { data: purchases = [] } = useQuery({ queryKey: ["purchases"], queryFn: async () => (await api.get("/purchases")).data });
  const { data: catalogue = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const applyProduct = (i, name) => {
    const match = catalogue.find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
    if (match) setItem(i, { product_name: name, unit: match.unit || "Piece", cost: match.last_cost ?? "" });
    else setItem(i, { product_name: name });
  };

  const setItem = (i, patch) =>
    setItems(items.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, ...patch };
      if ("net_price" in patch) {
        const q = Number(next.quantity || 0), n = Number(next.net_price || 0);
        if (q && n) next.cost = +(n / q).toFixed(2);
      } else if ("cost" in patch) {
        const q = Number(next.quantity || 0), c = Number(next.cost || 0);
        if (q && c) next.net_price = +(q * c).toFixed(2);
      } else if ("quantity" in patch) {
        const q = Number(next.quantity || 0), c = Number(next.cost || 0), n = Number(next.net_price || 0);
        if (q && c) next.net_price = +(q * c).toFixed(2);
        else if (q && n) next.cost = +(n / q).toFixed(2);
      }
      return next;
    }));

  const itemsTotal = useMemo(() => items.reduce((s, it) => s + Number(it.net_price || 0), 0), [items]);
  const finalTotal = useMemo(() => +(itemsTotal + Number(expense || 0) + Number(roundOff || 0)).toFixed(2), [itemsTotal, expense, roundOff]);
  const paid = payType === "credit" ? 0 : methods.reduce((s, m) => s + Number(m.amount || 0), 0);
  const balance = +(finalTotal - paid).toFixed(2);

  const [search, setSearch] = useState("");
  const [confirmDup, setConfirmDup] = useState(false);
  const dupBill = useMemo(() => {
    const bn = billNumber.trim().toLowerCase();
    if (!bn || !supplierId) return false;
    return purchases.some((p) => p.supplier_id === supplierId && (p.bill_number || "").trim().toLowerCase() === bn && p.id !== editId);
  }, [purchases, supplierId, billNumber, editId]);
  const shownPurchases = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return purchases;
    return purchases.filter((p) => (p.bill_number || "").toLowerCase().includes(s) || (p.supplier_name || "").toLowerCase().includes(s) || (p.purchase_no || "").toLowerCase().includes(s));
  }, [purchases, search]);

  const reset = () => {
    setSupplierId(""); setDate(today()); setBillNumber("");
    setItems([{ ...blankItem }]); setExpense(""); setRoundOff(""); setMemo("");
    setPayType("credit"); setMethods([]); setEditId(null);
  };

  const loadForEdit = (p) => {
    setEditId(p.id);
    setSupplierId(p.supplier_id);
    setDate(p.date || today());
    setBillNumber(p.bill_number || "");
    setItems(p.items?.length ? p.items.map((it) => ({
      product_name: it.product_name, unit: it.unit,
      quantity: it.quantity, cost: it.cost, net_price: it.net_price,
    })) : [{ ...blankItem }]);
    setExpense(p.expense || "");
    setRoundOff(p.round_off || "");
    setMemo(p.memo || "");
    setPayType(p.payment_type || "cash");
    setMethods((p.payments || []).filter((m) => m.mode !== "settlement"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        supplier_id: supplierId, date, bill_number: billNumber,
        items: items.filter((it) => it.product_name).map((it) => ({
          product_name: it.product_name, unit: it.unit,
          quantity: Number(it.quantity || 0), cost: Number(it.cost || 0), net_price: Number(it.net_price || 0),
        })),
        expense: Number(expense || 0), round_off: Number(roundOff || 0), total: finalTotal,
        memo, payment_type: payType,
        payments: payType === "credit" ? [] : methods.map((m) => ({ ...m, amount: Number(m.amount || 0) })),
      };
      return editId ? api.put(`/purchases/${editId}`, body) : api.post("/purchases", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchases"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(editId ? "Purchase updated" : "Purchase saved");
      reset();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/purchases/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["purchases"] }); toast.success("Deleted"); },
  });

  const statusColor = { paid: "bg-success/15 text-success", partial: "bg-accent/20 text-accent-foreground", unpaid: "bg-destructive/15 text-destructive" };

  return (
    <div>
      <PageTitle title={editId ? "Edit Purchase" : "Purchase Entry"} subtitle={editId ? "Correcting an existing bill" : "Record purchases under a supplier"} />

      {isAdmin && (
      <Card className="p-5 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <Label>Supplier *</Label>
            <Popover open={supOpen} onOpenChange={setSupOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="mt-1.5 w-full justify-between font-normal" data-testid="purchase-supplier">
                  <span className={cn(!supplierId && "text-muted-foreground")}>
                    {supplierId ? (() => { const s = suppliers.find((x) => x.id === supplierId); return s ? `${s.supplier_code} — ${s.name}` : "Select supplier"; })() : "Select supplier"}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                  <CommandInput placeholder="Type supplier name or code…" data-testid="purchase-supplier-search" />
                  <CommandList>
                    <CommandEmpty>No supplier found.</CommandEmpty>
                    <CommandGroup>
                      {suppliers.map((s) => (
                        <CommandItem key={s.id} value={`${s.supplier_code} ${s.name}`}
                          onSelect={() => { setSupplierId(s.id); setSupOpen(false); }} data-testid={`supplier-opt-${s.supplier_code}`}>
                          <Check className={cn("h-4 w-4", supplierId === s.id ? "opacity-100" : "opacity-0")} />
                          {s.supplier_code} — {s.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div><Label>Purchase Date</Label><div className="mt-1.5"><DatePicker value={date} onChange={setDate} testid="purchase-date" /></div></div>
          <div><Label>Bill Number</Label><Input value={billNumber} onChange={(e) => setBillNumber(e.target.value)} className="mt-1.5" data-testid="purchase-billno" /></div>
        </div>
        {dupBill && (
          <p className="mt-3 flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="dup-bill-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" /> Bill number "{billNumber}" is already recorded for this supplier.
          </p>
        )}

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /> Products</h3>
            <Button size="sm" variant="outline" onClick={() => setItems([...items, { ...blankItem }])} data-testid="add-item"><Plus className="h-4 w-4" /> Add Item</Button>
          </div>
          <div className="overflow-x-auto ssacc-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-2 min-w-[180px]">Product</th>
                <th className="py-2 px-2 w-28">Unit</th>
                <th className="py-2 px-2 w-24">Qty</th>
                <th className="py-2 px-2 w-28">Cost (₹)</th>
                <th className="py-2 px-2 w-32">Net Price (₹)</th>
                <th className="w-10"></th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} data-testid={`item-row-${i}`}>
                    <td className="py-1.5 pr-2"><Input list="product-catalogue" value={it.product_name} onChange={(e) => applyProduct(i, e.target.value)} placeholder="Type or pick a saved product" data-testid={`item-name-${i}`} /></td>
                    <td className="py-1.5 px-2">
                      <Select value={it.unit} onValueChange={(v) => setItem(i, { unit: v })}>
                        <SelectTrigger data-testid={`item-unit-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-2"><Input type="number" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid={`item-qty-${i}`} /></td>
                    <td className="py-1.5 px-2"><Input type="number" value={it.cost} onChange={(e) => setItem(i, { cost: e.target.value })} data-testid={`item-cost-${i}`} /></td>
                    <td className="py-1.5 px-2"><Input type="number" value={it.net_price} onChange={(e) => setItem(i, { net_price: e.target.value })} data-testid={`item-net-${i}`} /></td>
                    <td className="text-center">
                      {items.length > 1 && <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <datalist id="product-catalogue">{catalogue.map((p) => <option key={p.name} value={p.name} />)}</datalist>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Other Expense (₹)</Label><Input type="number" value={expense} onChange={(e) => setExpense(e.target.value)} className="mt-1.5" data-testid="purchase-expense" /></div>
              <div><Label>Round Off (₹)</Label><Input type="number" value={roundOff} onChange={(e) => setRoundOff(e.target.value)} className="mt-1.5" data-testid="purchase-roundoff" /></div>
            </div>
            <div><Label>Memo</Label><Textarea value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1.5" data-testid="purchase-memo" /></div>
            <div>
              <Label>Payment Type</Label>
              <Select value={payType} onValueChange={(v) => { setPayType(v); if (v === "credit") setMethods([]); }}>
                <SelectTrigger className="mt-1.5" data-testid="purchase-paytype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="credit">Credit</SelectItem><SelectItem value="cash">Cash / Paid</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl bg-muted/40 p-5 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Items Total</span><span className="data-num font-medium">{inr(itemsTotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Expense</span><span className="data-num">{inr(expense)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Round Off</span><span className="data-num">{inr(roundOff)}</span></div>
            <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold">Final Total</span><span className="font-display text-xl font-bold text-primary data-num" data-testid="purchase-total">{inr(finalTotal)}</span></div>

            {payType === "cash" && (
              <div className="pt-3">
                <Label className="text-xs">Payment Details (partial allowed)</Label>
                <div className="mt-2"><PaymentMethods methods={methods} setMethods={setMethods} modes={["cash", "neft", "cheque", "card", "upi"]} testidPrefix="ppm" /></div>
              </div>
            )}
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-muted-foreground">Balance</span>
              <span className={`font-bold data-num ${balance > 0 ? "text-destructive" : "text-success"}`} data-testid="purchase-balance">{inr(balance)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={reset}>{editId ? "Cancel Edit" : "Reset"}</Button>
          <Button onClick={() => { if (dupBill) setConfirmDup(true); else save.mutate(); }} disabled={!supplierId || save.isPending} data-testid="save-purchase">
            {save.isPending ? "Saving…" : editId ? "Update Purchase" : "Save Purchase"}
          </Button>
        </div>
      </Card>
      )}

      <PageTitle title="Recent Purchases" subtitle={`${purchases.length} bills`}>
        <Button variant="outline" onClick={() => downloadReport("/reports/purchases/export", { fmt: "pdf" }, "purchases")}><FileDown className="h-4 w-4" /> PDF</Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/purchases/export", { fmt: "excel" }, "purchases")}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
      </PageTitle>
      <Card className="overflow-hidden">
        <div className="p-3 border-b">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by bill number…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="purchase-search" />
          </div>
        </div>
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Bill No</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shownPurchases.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">{search ? "No bills match your search." : "No purchases yet."}</TableCell></TableRow>
              ) : shownPurchases.map((p) => (
                <TableRow key={p.id} data-testid={`purchase-row-${p.purchase_no}`}>
                  <TableCell className="font-semibold text-primary">{billNo(p.bill_number)}</TableCell>
                  <TableCell>{fmtDate(p.date)}</TableCell>
                  <TableCell>{p.supplier_name}</TableCell>
                  <TableCell className="text-right data-num">{inr(p.total)}</TableCell>
                  <TableCell className="text-right data-num">{inr(p.amount_paid)}</TableCell>
                  <TableCell className="text-right data-num font-medium">{inr(p.balance)}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[p.status]}`}>{p.status}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <Button size="icon" variant="ghost" onClick={() => setViewBill(p)} data-testid={`view-${p.purchase_no}`}><Eye className="h-4 w-4" /></Button>
                      {isAdmin && <><Button size="icon" variant="ghost" onClick={() => loadForEdit(p)} data-testid={`edit-${p.purchase_no}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del.mutate(p.id)}><Trash2 className="h-4 w-4" /></Button></>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!viewBill} onOpenChange={(o) => !o && setViewBill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{billNo(viewBill?.bill_number)} — {viewBill?.supplier_name}</DialogTitle>
            <DialogDescription>Purchase bill details, line items and payment history.</DialogDescription>
          </DialogHeader>
          {viewBill && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-4 text-muted-foreground">
                <span>Date: <b className="text-foreground">{fmtDate(viewBill.date)}</b></span>
                <span>Bill No: <b className="text-foreground">{viewBill.bill_number || "—"}</b></span>
                <Badge variant="outline">{viewBill.payment_type?.toUpperCase()}</Badge>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                <TableBody>
                  {viewBill.items?.map((it, i) => (
                    <TableRow key={i}><TableCell>{it.product_name}</TableCell><TableCell>{it.unit}</TableCell><TableCell className="text-right">{num(it.quantity)}</TableCell><TableCell className="text-right">{inr(it.cost)}</TableCell><TableCell className="text-right">{inr(it.net_price)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end gap-6">
                <div className="text-right"><div className="text-muted-foreground">Total</div><div className="font-bold data-num">{inr(viewBill.total)}</div></div>
                <div className="text-right"><div className="text-muted-foreground">Paid</div><div className="font-bold data-num text-success">{inr(viewBill.amount_paid)}</div></div>
                <div className="text-right"><div className="text-muted-foreground">Balance</div><div className="font-bold data-num text-destructive">{inr(viewBill.balance)}</div></div>
              </div>
              {viewBill.payments?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Payments</div>
                  {viewBill.payments.map((pm, i) => (
                    <div key={i} className="flex justify-between border-b py-1"><span>{pm.mode?.toUpperCase()} {pm.payment_no ? `(${pm.payment_no})` : ""}</span><span className="data-num">{inr(pm.amount)}</span></div>
                  ))}
                </div>
              )}
              {viewBill.memo && <div className="text-muted-foreground">Memo: {viewBill.memo}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDup} onOpenChange={setConfirmDup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate bill number</AlertDialogTitle>
            <AlertDialogDescription>Bill number "{billNumber}" is already recorded for this supplier. Do you still want to save this purchase?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmDup(false); save.mutate(); }} data-testid="confirm-dup-save">Save Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
